import type { Surface } from '@funkit/connect-core'
import { useEffect, useRef } from 'react'

import { FiatStatusScreen } from '../FiatStatusScreen'
import { isStripeCancellation } from './stripeSessionAuth'
import { logger } from '../../logging/logger'
import { useFunkitTranslation } from '../../providers/FunkitI18nProvider'
import type { SurfaceAdapter, SurfaceContext } from './adapter'
import type {
  StripeOnrampClient,
  StripePlatformPayParams,
} from './stripeOnramp'
import {
  redeemStripeSeamlessSignIn,
  stripeSeamlessClientSecret,
} from './stripeSeamlessSignIn'

interface StripePayParams {
  action: 'CREATE_CRYPTO_PAYMENT_TOKEN'
  walletAddress: string
  destinationNetwork: string
  merchantCountryCode: string
  currencyCode: string
  amount: string
  label?: string
}

export function matchStripePayParams(
  surface: Surface,
): StripePayParams | undefined {
  if (surface.kind !== 'PAY_SHEET' || surface.provider !== 'STRIPE') {
    return undefined
  }
  const params: unknown = surface.params
  if (params === null || typeof params !== 'object') {
    return undefined
  }
  const record = params as Record<string, unknown>
  if (
    record.action !== 'CREATE_CRYPTO_PAYMENT_TOKEN' ||
    typeof record.walletAddress !== 'string' ||
    record.walletAddress.length === 0 ||
    typeof record.destinationNetwork !== 'string' ||
    record.destinationNetwork.length === 0 ||
    typeof record.merchantCountryCode !== 'string' ||
    record.merchantCountryCode.length === 0 ||
    typeof record.currencyCode !== 'string' ||
    record.currencyCode.length === 0 ||
    typeof record.amount !== 'string' ||
    record.amount.length === 0
  ) {
    return undefined
  }
  return {
    action: 'CREATE_CRYPTO_PAYMENT_TOKEN',
    walletAddress: record.walletAddress,
    destinationNetwork: record.destinationNetwork,
    merchantCountryCode: record.merchantCountryCode,
    currencyCode: record.currencyCode,
    amount: record.amount,
    label: typeof record.label === 'string' ? record.label : undefined,
  }
}

/**
 * The buyer dismissed the provider's sheet.
 *
 * Not a failure: nothing is wrong, nothing needs reporting to support, and the
 * only right answer is the quote screen they came from. Carried as a code
 * rather than a separate channel because `onFailed` is the seam the surface
 * already owns, and its routing — back to entry, with "Try again" — is exactly
 * what a cancellation wants. The screen suppresses the alert for this one code.
 */
export const STRIPE_PAYMENT_CANCELLED = 'collect_apple_pay:cancelled'

/** Stripe's real native sheet, deliberately restricted to Apple Pay. */
export function stripePaymentAdapter(
  stripe: StripeOnrampClient,
  response?: object,
): SurfaceAdapter {
  return (surface) => {
    const params = matchStripePayParams(surface)
    if (params === undefined) {
      return undefined
    }
    // Read on claim, not inside the surface: the credential rides the response that carried this
    // pay sheet, so it is as old as the sheet and a later render must not pick up a newer one.
    const seamlessClientSecret = stripeSeamlessClientSecret(response)
    return {
      control: 'HANDOFF',
      render: (context) => (
        <StripeApplePaySurface
          stripe={stripe}
          params={params}
          seamlessClientSecret={seamlessClientSecret}
          context={context}
        />
      ),
    }
  }
}

function StripeApplePaySurface({
  stripe,
  params,
  seamlessClientSecret,
  context,
}: {
  stripe: StripeOnrampClient
  params: StripePayParams
  seamlessClientSecret: string | undefined
  context: SurfaceContext
}) {
  const { t } = useFunkitTranslation()
  const mounted = useRef(true)
  const started = useRef(false)
  const contextRef = useRef(context)
  contextRef.current = context

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (started.current) {
      return
    }
    started.current = true
    void collectApplePay(stripe, params, seamlessClientSecret)
      .then((token) => {
        if (mounted.current) {
          contextRef.current.report(stripePaymentReport(token))
        }
      })
      .catch((error: unknown) => {
        if (mounted.current) {
          const code = stripePaymentErrorCode(error)
          if (code === STRIPE_PAYMENT_CANCELLED) {
            logger.info('connect-rn:fiat:stripe_payment_cancelled', {
              stage: 'collect_apple_pay',
            })
          } else {
            // Every field Stripe offers, not just the code. `uncategorized_api_error` is its bucket
            // for anything without a specific code, so the one field we used to log was the one that
            // never said why — the same blind spot that hid a refused registration behind "Failed".
            logger.warn('connect-rn:fiat:stripe_payment_failed', {
              code,
              ...stripePaymentErrorDetail(error),
            })
          }
          contextRef.current.onFailed(code)
        }
      })
  }, [params, seamlessClientSecret, stripe])

  return (
    <FiatStatusScreen
      tone="waiting"
      title={t('fiat.stripeAuthorizing')}
      detail={t('fiat.stripeAuthorizingDetail')}
      // An Apple Pay sheet that never arrives leaves this waiting forever, and
      // without a close the buyer has no way off it.
      onClose={() => contextRef.current.onClose()}
    />
  )
}

export function stripePaymentReport(token: string) {
  return { cryptoPaymentToken: token }
}

export function stripePaymentErrorCode(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (error !== null && typeof error === 'object') {
    const candidate = error as {
      apiErrorCode?: unknown
      stripeErrorCode?: unknown
      onrampErrorType?: unknown
      message?: unknown
      code?: unknown
    }
    for (const code of [
      candidate.apiErrorCode,
      candidate.stripeErrorCode,
      candidate.onrampErrorType,
    ]) {
      if (typeof code === 'string' && code.length > 0) {
        return code
      }
    }
    if (
      candidate.code === 'Failed' &&
      typeof candidate.message === 'string' &&
      candidate.message.length > 0
    ) {
      return candidate.message
    }
    if (typeof candidate.code === 'string' && candidate.code.length > 0) {
      return candidate.code
    }
  }
  return 'stripe_payment_failed'
}

/**
 * A stage failure, with the provider's own error kept reachable as `cause`.
 *
 * The stage prefix is what the flow branches on, but building it out of a bare string threw away
 * every field Stripe attached — which is how `collect_apple_pay:uncategorized_api_error` logged
 * three copies of itself and nothing else. `cause` is set by assignment rather than the Error
 * options argument because Hermes does not carry it through reliably.
 */
function stageFailure(stage: string, error: unknown): Error {
  const failure = new Error(`${stage}:${stripePaymentErrorCode(error)}`)
  return Object.assign(failure, { cause: error })
}

/** Whatever the SDK attached to a rejection, so a bucket code is not the whole story. */
export function stripePaymentErrorDetail(
  error: unknown,
): Record<string, unknown> {
  if (error === null || typeof error !== 'object') {
    return { thrown: String(error) }
  }
  const candidate = error as Record<string, unknown>
  const detail: Record<string, unknown> = {}
  for (const field of [
    'code',
    'message',
    'onrampErrorType',
    'stripeErrorCode',
    'apiErrorCode',
    'apiErrorType',
    'developerMessage',
    'requestId',
    'declineCode',
    'localizedMessage',
    'userMessage',
    'docUrl',
    'type',
  ]) {
    if (candidate[field] !== undefined) {
      detail[field] = candidate[field]
    }
  }
  // The stage wrapper carries the provider's error under `cause`; without unwrapping it every
  // field above reads off the wrapper, which only ever holds the prefixed code.
  const cause = candidate.cause
  if (cause !== undefined && cause !== null) {
    detail.cause = stripePaymentErrorDetail(cause)
  }
  return detail
}

export async function collectApplePay(
  stripe: StripeOnrampClient,
  params: StripePayParams,
  seamlessClientSecret?: string,
): Promise<string> {
  logger.info('connect-rn:fiat:stripe_payment_stage_started', {
    stage: 'apple_pay_preflight',
  })
  if (!(await stripe.isPlatformPaySupported())) {
    throw new Error('apple_pay_unavailable')
  }
  logger.info('connect-rn:fiat:stripe_payment_stage_completed', {
    stage: 'apple_pay_preflight',
  })

  // Before `register_wallet`, not after it fails. The SDK reports a dead Link consumer session as
  // an opaque registration error and offers no way to test for one, so the only reliable move is to
  // wake the session while we still hold a credential for it. A refusal is not fatal here: the
  // interactive `SESSION_AUTH` round is the documented fallback.
  if (seamlessClientSecret !== undefined) {
    logger.info('connect-rn:fiat:stripe_payment_stage_started', {
      stage: 'seamless_signin',
    })
    const authenticated = await redeemStripeSeamlessSignIn(
      stripe,
      seamlessClientSecret,
    )
    logger.info('connect-rn:fiat:stripe_payment_stage_completed', {
      stage: 'seamless_signin',
      authenticated,
    })
  }

  logger.info('connect-rn:fiat:stripe_payment_stage_started', {
    stage: 'register_wallet',
    network: params.destinationNetwork,
  })
  const wallet = await stripe
    .registerWalletAddress(params.walletAddress, params.destinationNetwork)
    .catch((error: unknown) => {
      throw stageFailure('register_wallet', error)
    })
  if (wallet.error !== undefined) {
    throw stageFailure('register_wallet', wallet.error)
  }
  logger.info('connect-rn:fiat:stripe_payment_stage_completed', {
    stage: 'register_wallet',
  })

  const payment: StripePlatformPayParams = {
    applePay: {
      merchantCountryCode: params.merchantCountryCode,
      currencyCode: params.currencyCode,
      cartItems: [
        {
          paymentType: 'Immediate',
          label: params.label ?? 'Purchase',
          amount: params.amount,
        },
      ],
      requiredBillingContactFields: ['emailAddress', 'name', 'postalAddress'],
    },
  }
  logger.info('connect-rn:fiat:stripe_payment_stage_started', {
    stage: 'collect_apple_pay',
  })
  const collected = await stripe
    .collectPaymentMethod('PlatformPay', payment)
    .catch((error: unknown) => {
      throw stageFailure('collect_apple_pay', error)
    })
  if (collected.error !== undefined) {
    // Stripe spells a dismissed sheet as an error on this call, not as the
    // empty result below. Routed as a cancellation so the buyer lands back on
    // the quote instead of on "Apple Pay is unavailable".
    if (isStripeCancellation(collected.error)) {
      throw new Error(STRIPE_PAYMENT_CANCELLED)
    }
    throw stageFailure('collect_apple_pay', collected.error)
  }
  // Stripe reports three outcomes, not two: an error, a selection, or neither —
  // and neither means the buyer dismissed the sheet. Reading only `error` made a
  // cancellation look like a success, so the flow called `createCryptoPaymentToken`
  // with nothing collected and told the buyer Apple Pay was unavailable when they
  // had simply changed their mind.
  // `null` as well as `undefined`: the field is typed `displayData?: unknown`,
  // so a rail that answers with an explicit null was read as a selection.
  if (collected.displayData === undefined || collected.displayData === null) {
    throw new Error(STRIPE_PAYMENT_CANCELLED)
  }
  logger.info('connect-rn:fiat:stripe_payment_stage_completed', {
    stage: 'collect_apple_pay',
  })

  logger.info('connect-rn:fiat:stripe_payment_stage_started', {
    stage: 'create_payment_token',
  })
  const created = await stripe
    .createCryptoPaymentToken()
    .catch((error: unknown) => {
      throw stageFailure('create_payment_token', error)
    })
  if (created.cryptoPaymentToken === undefined) {
    throw created.error === undefined
      ? new Error('create_payment_token:stripe_payment_token_failed')
      : stageFailure('create_payment_token', created.error)
  }
  logger.info('connect-rn:fiat:stripe_payment_stage_completed', {
    stage: 'create_payment_token',
  })
  return created.cryptoPaymentToken
}
